import React, { startTransition, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compact, isEmpty, keys, map, pickBy } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import { connect } from 'react-redux';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
/* Hooks */
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
/* Models */
import { InventoryEntities } from 'client/data/models/inventory';
import {
  buildOptionWithOemCodeStyleAndPackagesPath,
  VehicleEntities,
  VehicleModel,
} from 'client/data/models/vehicle-v2';
/* Utils */
import {
  getFeaturesByCategories,
  getUpgradesOptions,
} from 'client/site-modules/shared/utils/private-party/features-and-specs';
import { transformColorsData } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/colors-and-options-step/colors-and-options-step-utils';
/* Constants */
import { TrackingConstant } from 'client/tracking/constant';
import { INSTALLED_UPGRADES } from 'client/site-modules/shared/constants/inventory/features-and-specs';
import { TOP_FEATURES_NUM } from 'client/site-modules/shared/utils/inventory/features-and-specs';
/* Components */
import { VdpModule } from 'site-modules/shared/components/inventory/vdp-module/vdp-module';
import { FeatureItem } from 'client/site-modules/shared/components/private-party/feature-item/feature-item';
import { InstalledUpgrades } from 'client/site-modules/shared/components/private-party/installed-upgrades/installed-upgrades';
import { FeaturesModal } from './features-modal';

import './features-and-specs-private.scss';

export function FeaturesAndSpecsPrivateUI({
  vinData = {},
  styleFeatures = null,
  className = '',
  isMobile = false,
  privateListingData = null,
  vehicleFeatures = null,
}) {
  const [defaultDisplay, setDefaultDisplay] = useState(undefined);
  const [isModalOpen, toggleModalOpen] = useToggle(false);
  const [upgrades, setUpgrades] = useState([]);

  useEffect(() => {
    if (privateListingData && vehicleFeatures) {
      const optionCodes = privateListingData?.tmvOptionCodes?.split(',') ?? [];
      const options = vehicleFeatures?.options;

      if (!isEmpty(options) && !isEmpty(optionCodes)) {
        startTransition(() =>
          setUpgrades(getUpgradesOptions({ tmvOptionCodes: optionCodes, options, packages: vehicleFeatures?.packages }))
        );
      }
    }
  }, [privateListingData, vehicleFeatures]);

  function toggleModal() {
    if (isModalOpen) {
      setDefaultDisplay(undefined);
    }
    toggleModalOpen();
  }

  function toggleModalIU() {
    if (!isModalOpen) {
      setDefaultDisplay(INSTALLED_UPGRADES);
    }
    toggleModal();
  }

  const { make, model, year, trim } = vinData?.vehicleInfo?.styleInfo ?? {};
  const featuresByCategories = styleFeatures?.features?.standard
    ? getFeaturesByCategories(styleFeatures.features.standard)
    : {};

  const topUpgrades = upgrades?.slice(0, TOP_FEATURES_NUM);
  const showInstalledUpgrades = !isEmpty(upgrades);
  const seeAllMap = {
    features: !isEmpty(featuresByCategories),
    upgrades: showInstalledUpgrades,
  };
  return (
    <VdpModule
      className={classNames('features-and-specs-private', 'bg-cool-gray-90', className)}
      headerTag="h2"
      sectionTitle="Features"
      titleClassName="heading-3"
      subCopy="as reported by owner"
      subCopyClassName="text-cool-gray-10 fw-medium mb-0_5"
    >
      <div className="fw-normal size-16">
        Features that are installed on this {compact([year, make, model, trim]).join(' ')}.
      </div>

      {showInstalledUpgrades && (
        <ul className="list-unstyled top-features mt-1 d-flex flex-wrap mb-1">
          {topUpgrades.map(({ id, name }) => (
            <li
              key={id}
              className="top-feature overlay-white-60 d-inline-block medium text-gray-darker fw-medium px-0_75 me-0_5 mb-0_5"
            >
              <span>{name}</span>
            </li>
          ))}
          <li className="more-btn d-flex">
            <button
              className="text-underline d-inline-block medium text-gray-darker fw-medium mb-0_25 border-0 c-pointer bg-transparent"
              onClick={toggleModal}
              aria-haspopup="dialog"
            >
              + more
              <span className="visually-hidden">{keys(pickBy(seeAllMap)).join(' & ')}</span>
            </button>
          </li>
        </ul>
      )}

      <VdpModule className="bg-white" indentationSize={1.5}>
        <div data-tracking-parent="edm-entry-features-specs">
          {map(featuresByCategories, (features, name) => (
            <FeatureItem key={name} id={name} features={features} />
          ))}

          {showInstalledUpgrades && <InstalledUpgrades options={upgrades} toggleModal={toggleModalIU} />}

          <Button
            className={classNames('modal-open-button size-16', {
              'w-100': isMobile,
              'mt-1_5': !showInstalledUpgrades,
            })}
            size="sm"
            color="outline-blue-50"
            data-tracking-id="vdp_view_content"
            data-subaction-name={TrackingConstant.VIEW_FEATURES}
            onClick={toggleModal}
            aria-haspopup="dialog"
          >
            All {keys(pickBy(seeAllMap)).join(' & ')}
          </Button>
        </div>

        <FeaturesModal
          features={featuresByCategories}
          isModalOpen={isModalOpen}
          toggleModal={toggleModal}
          defaultDisplay={defaultDisplay}
          isMobile={isMobile}
          options={upgrades}
          vinData={vinData}
        />
      </VdpModule>
    </VdpModule>
  );
}

FeaturesAndSpecsPrivateUI.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  styleFeatures: PropTypes.shape({}),
  className: PropTypes.string,
  isMobile: PropTypes.bool,
  privateListingData: PropTypes.shape({}),
  vehicleFeatures: VehicleEntities.VehicleFeatures,
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const stateToPropsConfig = {
  vehicleFeatures: bindToPath(
    ({ vinData }) => {
      const styleId = vinData?.vehicleInfo?.styleInfo?.styleId;
      return styleId ? buildOptionWithOemCodeStyleAndPackagesPath(styleId) : '';
    },
    VehicleModel,
    transformColorsData
  ),
};

export const FeaturesAndSpecsPrivate = connect(mapStateToProps)(
  connectToModel(FeaturesAndSpecsPrivateUI, stateToPropsConfig)
);
